import { Image } from 'expo-image';
import { LinearGradient } from 'expo-linear-gradient';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Animated, DimensionValue, Easing, StyleProp, View, ViewStyle } from 'react-native';

import { Text, createMqStyles, tokens } from '@fhs/ui';
export interface Tier {
  id: string;
  value: number;
  label?: string;
}

interface Props {
  currentValue: number;
  tiers: Tier[];
  style?: StyleProp<ViewStyle>;
  hideFlameIcon?: boolean;
  enableAnimation?: boolean;
}
const tierDotSize = 6;
const barHeight = 12;
const fireHeight = 28;
const fireWidth = 30;

export const tieredProgressBarConstants = {
  tierDotSize,
  barHeight,
  fireHeight,
  fireWidth,
};

const AnimatedLinearGradient = Animated.createAnimatedComponent(LinearGradient);

function Tier({ leftPosition, tier }) {
  const styles = useMqStyles();
  const [width, setWidth] = useState<number>();

  return (
    <View
      onLayout={e => {
        setWidth(e.nativeEvent.layout.width);
      }}
      style={[
        styles.tierContainer,
        {
          left: leftPosition,
          opacity: width ? 1 : 0,
          transform: [{ translateX: width ? -width / 2 : 0 }],
        },
      ]}
    >
      <View style={styles.tierDot} />
      <View key={tier.id} style={[styles.tierLabel]}>
        <Text.Ui size="sm" weight="semibold">
          {tier.label}
        </Text.Ui>
      </View>
    </View>
  );
}

function createLabel(value: number) {
  if (value >= 1000) {
    return `${Math.floor(value / 1000)}k`;
  }
  return value.toString();
}

export function TieredProgressBar({
  currentValue,
  tiers,
  style,
  enableAnimation = true,
  hideFlameIcon = false,
}: Props) {
  const styles = useMqStyles();
  const biggestTier = useMemo(() => {
    return tiers.find(tier => tier.value === Math.max(...tiers.map(tier => tier.value)));
  }, [tiers]);
  const biggestTierValue = biggestTier?.value ?? 0;
  const smallestTier = useMemo(() => {
    return tiers.find(tier => tier.value === Math.min(...tiers.map(tier => tier.value)));
  }, [tiers]);
  const smallestTierValue = smallestTier?.value ?? 0;
  const showFlameIcon = currentValue >= smallestTierValue && !hideFlameIcon;

  const slideAnim = useRef(new Animated.Value(0)).current;

  const calculatePercentage = useCallback(
    (value: number) => {
      return Math.max(
        0,
        Math.min(((value - smallestTierValue) / (biggestTierValue - smallestTierValue)) * 100, 100)
      );
    },
    [smallestTierValue, biggestTierValue]
  );
  const calculatePercentagePosition = useCallback(
    (value: number): DimensionValue => {
      return `${calculatePercentage(value)}%`;
    },
    [calculatePercentage]
  );

  const tierWithPositions = useMemo(() => {
    return tiers.map(tier => ({
      ...tier,
      label: tier.label || createLabel(tier.value),
      leftPosition: calculatePercentagePosition(tier.value),
    }));
  }, [tiers, calculatePercentagePosition]);

  useEffect(() => {
    if (enableAnimation) {
      Animated.timing(slideAnim, {
        toValue: calculatePercentage(currentValue),
        useNativeDriver: false,
        duration: 700,
        easing: Easing.out(Easing.cubic),
        delay: 100,
      }).start();
    }
  }, [currentValue, calculatePercentage, slideAnim, enableAnimation]);

  const biggestTierLabel = biggestTier?.label || createLabel(biggestTier?.value ?? 0);
  const smallestTierLabel = smallestTier?.label || createLabel(smallestTier?.value ?? 0);

  return (
    <View
      style={[
        styles.container,
        style,
        { paddingRight: biggestTierLabel.length * 3, paddingLeft: smallestTierLabel.length * 3 },
      ]}
    >
      <View>
        <View style={styles.barContainer}>
          <AnimatedLinearGradient
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            colors={[tokens.colors.$houseRed, tokens.colors.$houseYellow]}
            style={{
              height: '100%',
              width: slideAnim.interpolate({
                inputRange: [0, 100],
                outputRange: ['0%', '100%'],
              }),
            }}
          />
        </View>

        <View style={styles.tiersContainer}>
          <View style={styles.tiersOffsetContainer}>
            {tierWithPositions.map(tier => (
              <Tier key={tier.id} tier={tier} leftPosition={tier.leftPosition} />
            ))}
            {showFlameIcon && (
              <Animated.View
                style={[
                  {
                    left: slideAnim.interpolate({
                      inputRange: [0, 100],
                      outputRange: ['0%', '100%'],
                    }),
                  },
                  styles.fireIconContainer,
                ]}
              >
                <Image
                  source={require('./fire-emoji.png')}
                  style={[styles.fireIcon, { height: fireHeight }]}
                  contentFit="contain"
                />
              </Animated.View>
            )}
          </View>
        </View>
      </View>
    </View>
  );
}

const useMqStyles = createMqStyles({
  container: {
    $base: {
      width: '100%',
      overflow: 'hidden',
      paddingBottom: 30,
      paddingTop: fireHeight - barHeight,
    },
  },
  barContainer: {
    $base: {
      height: barHeight,
      borderRadius: 200,
      backgroundColor: tokens.colors.$blackOpacity10,
      overflow: 'hidden',
    },
  },
  tiersOffsetContainer: {
    $base: {
      marginHorizontal: tierDotSize,
    },
  },
  tierContainer: {
    $base: {
      justifyContent: 'center',
      alignItems: 'center',
      position: 'absolute',
    },
  },
  tierDot: {
    $base: {
      width: tierDotSize,
      height: tierDotSize,
      borderRadius: 100,
      backgroundColor: tokens.colors.$white,
    },
  },
  tiersContainer: {
    $base: {
      position: 'absolute',
      width: '100%',
      paddingTop: barHeight / 2 - tierDotSize / 2,
    },
  },
  tierLabel: {
    $base: {
      paddingTop: 10,
    },
  },
  fireIconContainer: {
    $base: {
      position: 'absolute',
      top: tierDotSize + 2,
      transform: [{ translateX: -fireWidth / 2 }, { translateY: -fireHeight }],
    },
  },
  fireIcon: {
    $base: {
      width: fireWidth,
    },
  },
});
